<template>
  <dashboard-layout :loading="initialLoading" loading-text="正在加载图像工作台">
    <div class="studio-page">
      <div class="page-head">
        <div class="title-block">
          <h2>图像工作台</h2>
          <p>文生图、改图都在这里异步执行（单张约 40~300 秒），可同时提交多个任务；所有图片统一进入图像库，角色卡参考图可直接从图像库选择</p>
        </div>
        <el-upload action="#" :show-file-list="false" :before-upload="onUpload">
          <el-button round icon="el-icon-upload2" :loading="uploading">上传图片到图像库</el-button>
        </el-upload>
      </div>

      <!-- 提交任务 -->
      <div class="panel">
        <div class="mode-row">
          <el-radio-group v-model="mode" size="small">
            <el-radio-button label="generate">文生图</el-radio-button>
            <el-radio-button label="edit">改图</el-radio-button>
          </el-radio-group>
          <span class="mode-tip">{{ mode === "generate" ? "输入提示词直接生成新图" : "从图像库选一张原图，再用提示词修改" }}</span>
        </div>
        <div v-if="mode === 'edit'" class="source-row">
          <div class="source-preview">
            <img v-if="sourceAsset" :src="sourceAsset.public_url" alt="" />
            <div v-else class="source-empty"><i class="el-icon-picture-outline"></i><span>未选原图</span></div>
          </div>
          <div class="source-meta">
            <p v-if="sourceAsset">已选原图 #{{ sourceAsset.id }}（{{ kindLabel(sourceAsset.kind) }}）</p>
            <p v-else>请在下方图像库中点击「以此改图」选择原图</p>
            <el-button v-if="sourceAsset" size="mini" round @click="sourceAsset = null">取消选择</el-button>
          </div>
        </div>
        <div class="prompt-row">
          <el-input
            type="textarea"
            :rows="3"
            v-model="prompt"
            :placeholder="mode === 'generate' ? '描述你想要的画面，例如：22岁女生，黑色长直发，米色针织衫，咖啡店窗边，自然光' : '描述要怎么改，例如：把背景换成夜晚的城市街道，整体偏暖色调'"
          />
          <el-button type="primary" round :loading="submitting" class="submit-btn" @click="submitJob">
            {{ mode === "generate" ? "提交生图任务" : "提交改图任务" }}
          </el-button>
        </div>
      </div>

      <!-- 任务列表 -->
      <div v-if="jobs.length" class="panel">
        <h3 class="panel-title">生成任务<small>提交后可离开页面，回来会继续显示进度</small></h3>
        <div class="job-list">
          <div v-for="job in jobs" :key="job.id" class="job-item">
            <div class="job-thumb">
              <img v-if="job.result_asset" :src="job.result_asset.public_url" alt="" @click="previewUrl = job.result_asset.public_url" />
              <i v-else-if="job.status === 'failed'" class="el-icon-warning-outline fail"></i>
              <i v-else class="el-icon-loading"></i>
            </div>
            <div class="job-info">
              <div class="job-line">
                <span class="job-status" :class="job.status">{{ statusLabel(job.status) }}</span>
                <span class="job-kind">{{ job.kind === "edit" ? "改图" : "文生图" }}</span>
                <em>{{ (job.created_at || "").slice(5, 16).replace("T", " ") }}</em>
              </div>
              <p class="job-prompt" :title="job.prompt">{{ job.prompt }}</p>
              <p v-if="job.status === 'failed'" class="job-error">{{ job.error_message || "生成失败" }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 图像库 -->
      <div class="panel">
        <h3 class="panel-title">图像库<small>共 {{ assets.length }} 张，生成、编辑、上传的图片都在这里</small></h3>
        <div v-if="!assets.length" class="empty-tip">图像库还是空的，提交一个生图任务或上传一张图片吧。</div>
        <div class="asset-grid">
          <div v-for="asset in assets" :key="asset.id" class="asset-card">
            <div class="asset-cover" @click="previewUrl = asset.public_url">
              <img :src="asset.public_url" alt="" />
              <span class="asset-kind" :class="asset.kind">{{ kindLabel(asset.kind) }}</span>
            </div>
            <div class="asset-body">
              <p class="asset-prompt" :title="asset.prompt">{{ asset.prompt || "（无提示词）" }}</p>
              <div class="asset-foot">
                <small>{{ (asset.created_at || "").slice(0, 10) }}</small>
                <el-button size="mini" round @click="useAsSource(asset)">以此改图</el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 大图预览 -->
    <el-dialog :visible.sync="previewVisible" width="640px" custom-class="yw-dialog" title="图片预览">
      <img v-if="previewUrl" :src="previewUrl" alt="" style="width: 100%; border-radius: 12px" />
    </el-dialog>
  </dashboard-layout>
</template>

<script>
import { imageStudioApi } from "@/services/api";
import DashboardLayout from "@/components/DashboardLayout.vue";

export default {
  name: "ImageStudioView",
  components: { DashboardLayout },
  data() {
    return {
      mode: "generate",
      prompt: "",
      sourceAsset: null,
      submitting: false,
      uploading: false,
      jobs: [],
      assets: [],
      previewUrl: "",
      pollTimer: null,
      initialLoading: true,
    };
  },
  computed: {
    previewVisible: {
      get() {
        return !!this.previewUrl;
      },
      set(v) {
        if (!v) this.previewUrl = "";
      },
    },
  },
  created() {
    this.load();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    async load() {
      try {
        const [jobs, assets] = await Promise.all([imageStudioApi.listJobs(), imageStudioApi.listAssets()]);
        this.jobs = jobs || [];
        this.assets = assets || [];
        this.ensurePolling();
      } catch (e) {
        this.$message.error(e.message || "加载图像库失败");
      } finally {
        this.initialLoading = false;
      }
    },
    async submitJob() {
      if (!this.prompt.trim()) {
        this.$message.warning("请先填写提示词");
        return;
      }
      if (this.mode === "edit" && !this.sourceAsset) {
        this.$message.warning("改图需要先从图像库选择原图");
        return;
      }
      this.submitting = true;
      try {
        const job = await imageStudioApi.createJob({
          kind: this.mode,
          prompt: this.prompt.trim(),
          source_asset_id: this.mode === "edit" ? this.sourceAsset.id : 0,
        });
        this.jobs.unshift(job);
        this.prompt = "";
        this.$message.success("任务已提交，正在后台生成（约 40~300 秒），可继续提交其他任务");
        this.ensurePolling();
      } catch (e) {
        this.$message.error(e.message || "提交失败");
      } finally {
        this.submitting = false;
      }
    },
    async onUpload(file) {
      this.uploading = true;
      try {
        const asset = await imageStudioApi.uploadAsset(file);
        this.assets.unshift(asset);
        this.$message.success("已加入图像库");
      } catch (e) {
        this.$message.error(e.message || "上传失败");
      } finally {
        this.uploading = false;
      }
      return false;
    },
    useAsSource(asset) {
      this.mode = "edit";
      this.sourceAsset = asset;
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    hasActiveJobs() {
      return this.jobs.some((j) => j.status === "pending" || j.status === "running");
    },
    ensurePolling() {
      if (this.pollTimer || !this.hasActiveJobs()) return;
      this.pollTimer = setInterval(this.pollJobs, 3000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async pollJobs() {
      const active = this.jobs.filter((j) => j.status === "pending" || j.status === "running");
      if (!active.length) {
        this.stopPolling();
        return;
      }
      let finished = false;
      for (const job of active) {
        try {
          const fresh = await imageStudioApi.getJob(job.id);
          const index = this.jobs.findIndex((j) => j.id === fresh.id);
          if (index >= 0) this.$set(this.jobs, index, fresh);
          if (fresh.status === "done") finished = true;
          if (fresh.status === "failed") this.$message.error(`任务失败：${fresh.error_message || "未知错误"}`);
        } catch (e) {
          /* 单次轮询失败忽略，下一轮重试 */
        }
      }
      if (finished) {
        try {
          this.assets = (await imageStudioApi.listAssets()) || [];
        } catch (e) {
          /* ignore */
        }
      }
      if (!this.hasActiveJobs()) this.stopPolling();
    },
    statusLabel(status) {
      return { pending: "排队中", running: "生成中", done: "已完成", failed: "失败" }[status] || status;
    },
    kindLabel(kind) {
      return { generated: "生成", edited: "改图", uploaded: "上传" }[kind] || kind;
    },
  },
};
</script>

<style scoped lang="scss">
.studio-page {
  .page-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;

    .title-block {
      h2 {
        margin: 0 0 6px;
        font-size: 26px;
        font-weight: 700;
        color: #1a1a1a;
      }

      p {
        margin: 0;
        max-width: 640px;
        color: #999;
        font-size: 13px;
        line-height: 1.6;
      }
    }
  }

  .panel {
    margin-bottom: 18px;
    padding: 18px 20px;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);

    .panel-title {
      margin: 0 0 14px;
      font-size: 16px;
      font-weight: 700;
      color: #1a1a1a;

      small {
        margin-left: 10px;
        color: #bbb;
        font-size: 12px;
        font-weight: 400;
      }
    }
  }

  .mode-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;

    .mode-tip {
      color: #bbb;
      font-size: 12px;
    }
  }

  .source-row {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 14px;
    padding: 10px 12px;
    border-radius: 12px;
    background: #f8f6fb;

    .source-preview {
      width: 72px;
      height: 72px;
      flex-shrink: 0;
      border-radius: 10px;
      overflow: hidden;
      background: #eeeafe;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .source-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: #e3b3c8;
        font-size: 11px;

        i {
          margin-bottom: 4px;
          font-size: 20px;
        }
      }
    }

    .source-meta {
      p {
        margin: 0 0 6px;
        color: #777;
        font-size: 13px;
      }
    }
  }

  .prompt-row {
    display: flex;
    align-items: flex-end;
    gap: 12px;

    .el-textarea {
      flex: 1;
    }

    .submit-btn {
      flex-shrink: 0;
      border: 0;
      background: linear-gradient(90deg, #c94225, #7255e9);
    }
  }

  .job-list {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .job-item {
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 10px 12px;
      border-radius: 12px;
      background: #faf7f9;

      .job-thumb {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 64px;
        height: 64px;
        flex-shrink: 0;
        border-radius: 10px;
        overflow: hidden;
        background: #eeeafe;
        color: #816bef;
        font-size: 20px;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          cursor: zoom-in;
        }

        .fail {
          color: #f56c6c;
        }
      }

      .job-info {
        flex: 1;
        min-width: 0;

        .job-line {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 4px;

          .job-status {
            padding: 1px 10px;
            border-radius: 10px;
            color: #fff;
            font-size: 11px;
            background: #faad14;

            &.running {
              background: #409eff;
            }

            &.done {
              background: #52c41a;
            }

            &.failed {
              background: #f56c6c;
            }
          }

          .job-kind {
            color: #816bef;
            font-size: 12px;
          }

          em {
            color: #bbb;
            font-size: 11px;
            font-style: normal;
          }
        }

        .job-prompt {
          margin: 0;
          overflow: hidden;
          color: #666;
          font-size: 13px;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .job-error {
          margin: 4px 0 0;
          color: #f56c6c;
          font-size: 12px;
        }
      }
    }
  }

  .empty-tip {
    padding: 40px 0;
    text-align: center;
    color: #bbb;
    font-size: 14px;
  }

  .asset-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 14px;

    .asset-card {
      border-radius: 14px;
      overflow: hidden;
      background: #faf7f9;
      transition: transform 0.2s, box-shadow 0.2s;

      &:hover {
        transform: translateY(-3px);
        box-shadow: 0 10px 24px rgba(114, 85, 233, 0.12);
      }

      .asset-cover {
        position: relative;
        aspect-ratio: 1 / 1;
        cursor: zoom-in;
        background: #eeeafe;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .asset-kind {
          position: absolute;
          left: 8px;
          top: 8px;
          padding: 2px 8px;
          border-radius: 8px;
          color: #fff;
          font-size: 11px;
          background: rgba(114, 85, 233, 0.85);

          &.edited {
            background: rgba(64, 158, 255, 0.85);
          }

          &.uploaded {
            background: rgba(82, 196, 26, 0.85);
          }
        }
      }

      .asset-body {
        padding: 8px 10px 10px;

        .asset-prompt {
          margin: 0 0 8px;
          overflow: hidden;
          color: #777;
          font-size: 12px;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .asset-foot {
          display: flex;
          justify-content: space-between;
          align-items: center;

          small {
            color: #bbb;
            font-size: 11px;
          }
        }
      }
    }
  }

  @media (max-width: 768px) {
    .page-head {
      flex-direction: column;
      gap: 10px;
    }

    .prompt-row {
      flex-direction: column;
      align-items: stretch;
    }

    .asset-grid {
      grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    }
  }
}
</style>

<style scoped lang="scss">
.studio-page {
  min-width: 0;

  .page-head {
    min-width: 0;

    .title-block {
      min-width: 0;

      h2 {
        color: var(--xyo-ink-strong);
      }

      p {
        color: var(--xyo-muted);
      }
    }
  }

  .panel {
    min-width: 0;
    border: 1px solid var(--xyo-line);
    background: var(--xyo-surface-raised);
    box-shadow: var(--xyo-shadow-sm);

    .panel-title {
      color: var(--xyo-ink-strong);

      small {
        color: var(--xyo-muted);
      }
    }
  }

  .mode-row .mode-tip,
  .source-row .source-meta p,
  .job-list .job-item .job-info .job-line em,
  .job-list .job-item .job-info .job-prompt,
  .empty-tip,
  .asset-grid .asset-card .asset-body .asset-prompt,
  .asset-grid .asset-card .asset-body .asset-foot small {
    color: var(--xyo-muted);
  }

  .source-row {
    min-width: 0;
    border: 1px solid var(--xyo-line);
    background: var(--xyo-accent-pale);

    .source-preview {
      background: var(--xyo-surface-muted);

      .source-empty {
        color: var(--xyo-primary);
      }
    }

    .source-meta {
      min-width: 0;
    }
  }

  .prompt-row {
    min-width: 0;

    .el-textarea {
      min-width: 0;
    }

    .submit-btn {
      background: var(--xyo-primary);
    }
  }

  .job-list .job-item {
    min-width: 0;
    border: 1px solid var(--xyo-line);
    background: var(--xyo-surface-muted);

    .job-thumb {
      color: var(--xyo-primary);
      background: var(--xyo-accent-pale);

      .fail {
        color: var(--xyo-danger);
      }
    }

    .job-info {
      .job-line {
        min-width: 0;

        .job-status {
          background: var(--xyo-warning);

          &.running {
            background: var(--xyo-info);
          }

          &.done {
            background: var(--xyo-success);
          }

          &.failed {
            background: var(--xyo-danger);
          }
        }

        .job-kind {
          color: var(--xyo-primary);
        }
      }

      .job-error {
        color: var(--xyo-danger);
      }
    }
  }

  .asset-grid {
    min-width: 0;

    .asset-card {
      min-width: 0;
      border: 1px solid var(--xyo-line);
      background: var(--xyo-surface-muted);

      &:hover {
        box-shadow: var(--xyo-shadow-lg);
      }

      .asset-cover {
        background: var(--xyo-accent-pale);

        .asset-kind {
          background: var(--xyo-primary);

          &.edited {
            background: var(--xyo-info);
          }

          &.uploaded {
            background: var(--xyo-success);
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .studio-page {
    max-width: 100%;

    .page-head,
    .panel,
    .source-row,
    .prompt-row,
    .job-list,
    .job-list .job-item,
    .job-list .job-item .job-info,
    .asset-grid {
      min-width: 0;
      max-width: 100%;
    }

    .asset-grid {
      grid-template-columns: minmax(0, 1fr);
    }
  }
}
</style>
